import { EMPTY_ARRAY } from 'constants/defaults';
import { BUDGET_INTERVAL, BUDGET_TYPES } from 'constants/project';
import { enrichProjectWithBudgetAttributes } from 'factories/projects';
import type { BudgetEntity } from 'redux/modules/projects.types';
import { getIsAppInitialized } from 'redux/selectors/global';
import { getCanSeeTasks } from 'redux/selectors/subscriptions';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Account } from 'types/account';
import type {
  BudgetRecurrence,
  EnrichedProject,
  EnrichedProjectWithBudget,
  Project,
} from 'types/project';
import { naturalCompare } from 'utils/naturalSort';
import {
  calculateBudgetPercent,
  calculateBudgetProgress,
  calculateBudgetRemaining,
} from 'utils/project';
import { makeBudgetPeriodLabel } from 'utils/project.details';
import { makeFirstArgumentOptional } from 'utils/typesafe';
import { getAccountFeatures, getCurrentAccount, getRecurringBudgetsEnabled } from './accounts';
import { getActivityXhrStatus, getProjectActivities } from './activities';
import { getPinnedProjectIdsSet } from './pins';
import { getProjectSubscriptions } from './projectSubscriptions';
import { getPreferredProjectsView, getProjectVariantMap } from './projects';
import { getCurrentlyViewedProjectId } from './projects.shared';
import { getReportQuery } from './superreports';
import { getUser, getUserPermissions } from './user';
import { enrichProjectVariantOrNull } from './utils/projects';

export const getCurrentlyViewedProject = createSelector(
  [getCurrentlyViewedProjectId, getProjectVariantMap, getPinnedProjectIdsSet],
  (
    projectId,
    projectsMap,
    pinnedProjectIdsSet
  ): EnrichedProject | EnrichedProjectWithBudget | undefined => {
    const project = enrichProjectVariantOrNull(projectsMap[projectId!], pinnedProjectIdsSet);
    if (!project?.fullProject) return project;

    return enrichProjectWithBudgetAttributes(project);
  }
);

export const getProjectDetailsPage = createSelector(
  [
    getUser,
    getCurrentlyViewedProject,
    getProjectActivities,
    getActivityXhrStatus,
    getProjectSubscriptions,
    getReportQuery,
    getRecurringBudgetsEnabled,
    getCurrentAccount,
    getIsAppInitialized,
    getAccountFeatures,
    getUserPermissions,
    getCanSeeTasks,
    getPreferredProjectsView,
  ],
  (
    user,
    project,
    activities,
    xhrStatus,
    isSubscribed,
    reportQuery,
    isRecurringBudgetsEnabled,
    currentAccount,
    isAppInitialized,
    features,
    permissions,
    canSeeTasks,
    defaultProjectsView
  ) => {
    const enhancedProject = makeFirstArgumentOptional(enrichProjectWithBudgetAttributes)(project);

    return {
      project: enhancedProject,
      activities, // NB: automatically scoped on props.match.params.project_id, ref activities selector
      xhrStatus,
      reportQuery,
      isRecurringBudgetsEnabled,
      account: currentAccount,
      isAppInitialized,
      permissions,
      ui: {
        canSeeTasks,
        isSubscribeLockedFeature: !features.notifications_project_budget,
        showSubscribe: !isSubscribed,
        showUnsubscribe: isSubscribed,
        isProjectsDashboardFeatureLocked: !features.project_dashboard,
        defaultProjectsView,
      },
    };
  }
);

const readProjectBudgetEntities = (state: IState) => state.projects.budgets.entities;
const readProjectBudgetIntervalsByProjectId = (state: IState) =>
  state.projects.budgets.intervalsByProjectId;
const readProjectBudgetFetchStatus = (state: IState) => state.projects.budgets.fetchStatus;

const getProjectBudgetIntervals = createSelector(
  [getCurrentlyViewedProjectId, readProjectBudgetIntervalsByProjectId],
  (id, intervals) => {
    return (id && intervals[id]) || [];
  }
);

const getProjectBudgets = createSelector(
  [getCurrentlyViewedProjectId, getProjectBudgetIntervals, readProjectBudgetEntities],
  (projectId, intervals, projectBudgets): Array<BudgetEntity> => {
    const budgets = projectId && projectBudgets[projectId];

    if (!budgets) {
      return EMPTY_ARRAY;
    }

    return intervals
      .map(interval => budgets[interval])
      .sort((a, b) => naturalCompare(a.start_date, b.start_date))
      .reverse();
  }
);

const getIsLoadingBudgets = createSelector(
  [getCurrentlyViewedProjectId, readProjectBudgetFetchStatus],
  (id, fetchStatus) => {
    if (!id || !fetchStatus[id!]) {
      return false;
    }

    // @ts-expect-error This is a bug, inFlight does not exist in FetchStatus
    return fetchStatus[id].inFlight;
  }
);

interface BudgetRow {
  spentPercent: number;
  remaining: number;
  spent: number;
  loggedMinutes: number;
  key: string;
  label: string;
  exceeded: boolean;
}

const budgetRowLabel = (
  budgetRecurrence: BudgetRecurrence | null | undefined,
  budget: { key?: string } = {}
): string => {
  const recur = budgetRecurrence && budgetRecurrence.recur;

  const label =
    recur === BUDGET_INTERVAL.MONTH
      ? budget.key!
      : recur === BUDGET_INTERVAL.WEEK
        ? `Week ${budget.key}`
        : budget.key!;

  return label;
};

const budgetRow = (project: Project | undefined, budget: BudgetEntity): BudgetRow => {
  const seed = { ...project, ...budget.totals } as Project;
  const remaining = calculateBudgetRemaining(seed);
  const spent = calculateBudgetProgress(seed);
  const spentPercent = calculateBudgetPercent(seed);

  return {
    spentPercent,
    remaining: Math.abs(remaining),
    spent,
    loggedMinutes: seed?.duration?.total_minutes ?? 0,
    key: budget.key!,
    label: budgetRowLabel(project?.budget_recurrence, budget),
    exceeded: spentPercent > 100,
  };
};

interface BudgetPageUIState {
  isRecurringBudgetsEnabled: boolean;
  hasBudgetStarted: boolean;
  isLoadingBudgets: boolean;
  hasMoneyBudget: boolean;
  hasTimeBudget: boolean;
  hasBudgetEntries: boolean;
  budgetPeriod: string | null | undefined;
  tableHeaderColumns: Array<{
    column: string;
    name: string;
    isActive: boolean;
    alignRight: boolean;
  }>;
}

export interface ProjectBudgetPage {
  account: Account;
  budgets: Array<BudgetRow>;
  project: Project | undefined;
  ui: BudgetPageUIState;
}

export const getProjectBudgetsPage = createSelector(
  [
    getCurrentAccount,
    getRecurringBudgetsEnabled,
    getCurrentlyViewedProject,
    getProjectBudgets,
    getIsLoadingBudgets,
  ],
  (
    account: Account,
    isRecurringBudgetsEnabled,
    project,
    projectBudgets,
    isLoadingBudgets: boolean
  ): ProjectBudgetPage => {
    const fullProject = project?.fullProject ?? undefined;
    const budgetRecurrence = fullProject?.budget_recurrence;

    return {
      account,
      budgets:
        project == null
          ? []
          : projectBudgets.map((budget: BudgetEntity) => budgetRow(fullProject, budget)),
      project: project?.fullProject ?? undefined,
      ui: {
        hasMoneyBudget: fullProject?.budget_type === BUDGET_TYPES.MONEY,
        hasTimeBudget: fullProject?.budget_type === BUDGET_TYPES.HOUR,
        isRecurringBudgetsEnabled,
        hasBudgetStarted:
          !isRecurringBudgetsEnabled ||
          (!!budgetRecurrence?.start_date && new Date(budgetRecurrence.start_date) < new Date()),
        isLoadingBudgets,
        hasBudgetEntries: projectBudgets.length > 0,
        budgetPeriod: budgetRecurrence && makeBudgetPeriodLabel(budgetRecurrence),
        tableHeaderColumns: [
          {
            name: 'Period',
            column: 'period',
            isActive: false,
            alignRight: false,
          },
          {
            name: 'Budget spent',
            column: 'spend',
            isActive: false,
            alignRight: false,
          },
          {
            name: 'Logged total',
            column: 'logged',
            isActive: false,
            alignRight: true,
          },
          {
            name: 'Remaining',
            column: 'remaining',
            isActive: false,
            alignRight: true,
          },
          {
            name: 'Total',
            column: 'total',
            isActive: false,
            alignRight: true,
          },
        ],
      },
    };
  }
);
